import { BaseService } from '../../infra/base.service'
import type { TaxInvoiceEntity } from './tax-invoice.entity'
import type { TaxInvoicesRepository } from './tax-invoices.repository'

export class TaxInvoicesService extends BaseService<TaxInvoiceEntity> {
    constructor(private rep: TaxInvoicesRepository) {
        super(rep)
    }

    async sendNFSe(taxInvoiceId: string) {
        return await this.rep.sendNFSe(taxInvoiceId)
    }

    async cancelNFSe(taxInvoiceId: string) {
        return await this.rep.cancelNFSe(taxInvoiceId)
    }

    async syncNFSe(taxInvoiceId: string) {
        return await this.rep.syncNFSe(taxInvoiceId)
    }

    async getPDF(taxInvoiceId: string) {
        return await this.rep.getPDF(taxInvoiceId)
    }

    async getXML(taxInvoiceId: string) {
        return await this.rep.getXML(taxInvoiceId)
    }

    async getRPS(taxInvoiceId: string) {
        return await this.rep.getRPS(taxInvoiceId)
    }

    // Número da NFS-e. Notas do emissor nacional emitidas antes da correção no pos-api ficaram sem
    // resultNFSeNumber; para elas o número sai da chave de acesso (responseId), que tem 50 dígitos:
    // município (7) + ambiente (1) + tipo de inscrição (1) + CNPJ/CPF (14) + número (13) + ...
    nfseNumber(taxI: Partial<TaxInvoiceEntity>): string {
        const digitos = String(taxI?.resultNFSeNumber || '').replace(/\D/g, '')
        if (digitos) return String(parseInt(digitos, 10))
        const chave = String(taxI?.responseId || '')
        if (/^\d{50}$/.test(chave)) {
            const numero = parseInt(chave.substring(23, 36), 10)
            if (numero) return String(numero)
        }
        return ''
    }

    // Nome do arquivo baixado (PDF/XML) da NFS-e: YYYYMMDD_nnnn_nome-do-cliente[_homologacao].ext
    // - data de emissão (resultAt; sem ela, a do envio ou a de criação), no fuso local
    // - número da NFS-e com no mínimo 4 dígitos, sem cortar os maiores; sem número, a hora da emissão
    //   (HHMMSS) no lugar dele, para o nome continuar único
    // - nome do cliente sem acentos, em minúsculas, com hífen no lugar de qualquer outro caractere, até 40
    // - _homologacao para nota de teste: a numeração da homologação é própria e repetiria a de produção
    fileName(taxI: Partial<TaxInvoiceEntity>, personName: string, ext: 'pdf' | 'xml'): string {
        const pad = (n: number) => String(n).padStart(2, '0')
        const d = new Date(taxI?.resultAt || taxI?.requestAt || taxI?.createdAt || Date.now())
        const data = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`

        const nfseNumber = this.nfseNumber(taxI)
        const numero = nfseNumber ? nfseNumber.padStart(4, '0') : `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`

        const cliente =
            (personName || '')
                .normalize('NFD')
                .replace(/[̀-ͯ]/g, '')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+/, '')
                .substring(0, 40)
                .replace(/-+$/, '') || 'sem-cliente'

        const homologacao = taxI?.requestLog?.tpAmb === 2 ? '_homologacao' : ''
        return `${data}_${numero}_${cliente}${homologacao}.${ext}`
    }
}
